import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { AlertTriangle, FileQuestion, ShieldAlert, UserCog, Receipt, Briefcase, ChevronRight, Clock, CheckCircle2 } from "lucide-react";

function Card({ title, icon: Icon, testid, children, tone = "slate" }) {
  const ring = tone === "red" ? "border-red-200" : "border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${ring} bg-white p-5`}>
      <h3 className="mb-3 flex items-center gap-2 font-display text-base font-bold text-slate-900">
        <Icon className="h-4 w-4 text-slate-400" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ label, value, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick} className={`flex w-full items-center justify-between border-b border-slate-100 py-2 text-left text-sm last:border-0 ${onClick ? "hover:bg-slate-50" : ""}`}>
      <span className="min-w-0 truncate text-slate-600">{label}</span>
      <span className="flex shrink-0 items-center gap-1 font-semibold text-slate-900">{value}{onClick && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}</span>
    </button>
  );
}

export function OpsCommandCenter() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/admin/ops-summary").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const na = d.needs_attention;
  const cur = d.billing.currency;

  const attentionItems = [
    { k: "Passed deadlines", n: na.passed, to: "/admin/jobs?risk=passed", tone: "text-red-600" },
    { k: "Urgent deadlines", n: na.urgent, to: "/admin/jobs?risk=urgent", tone: "text-orange-600" },
    { k: "Integrity mismatches", n: na.integrity, to: "/admin/documents", tone: "text-red-600" },
    { k: "Open document requests", n: na.open_requests, to: "/admin/requests", tone: "text-amber-600" },
    { k: "Pending server approvals", n: na.pending_servers, to: "/admin/servers", tone: "text-amber-600" },
    { k: "Outstanding billing", n: na.outstanding_billing, to: "/admin/billing", tone: "text-amber-600" },
  ];

  return (
    <div className="space-y-5">
      <div data-testid="ops-needs-attention" className="rounded-xl border border-red-200 bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-red-900">
          <AlertTriangle className="h-5 w-5 text-red-600" /> Needs attention
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {attentionItems.map((a) => (
            <button key={a.k} data-testid={`attention-${a.k.split(" ")[0].toLowerCase()}`} onClick={() => nav(a.to)} className="rounded-lg border border-slate-100 p-3 text-left transition hover:bg-slate-50">
              <p className={`font-display text-2xl font-extrabold ${a.n > 0 ? a.tone : "text-slate-300"}`}>{a.n}</p>
              <p className="mt-0.5 text-xs text-slate-500">{a.k}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Assignment & service pipeline" icon={Briefcase} testid="ops-pipeline">
          <Row label="Awaiting assignment" value={d.orders.awaiting_assignment} onClick={() => nav("/admin/jobs?stage=awaiting_assignment")} testid="ops-stage-awaiting" />
          <Row label="Assigned · no verified activity" value={d.orders.assigned} onClick={() => nav("/admin/jobs?stage=assigned")} testid="ops-stage-assigned" />
          <Row label="Active · verified activity" value={d.orders.active} onClick={() => nav("/admin/jobs?stage=active")} testid="ops-stage-active" />
          <Row label="Completed" value={d.orders.completed ?? (d.orders.total - d.orders.current_active_work)} onClick={() => nav("/admin/jobs?stage=completed")} testid="ops-stage-completed" />
          <div className="mt-2 border-t border-slate-200 pt-1">
            <Row label="Current active work" value={d.orders.current_active_work} onClick={() => nav("/admin/jobs?stage=active_work")} testid="ops-current-active-work" />
            <Row label="Total accountable jobs" value={d.orders.total} onClick={() => nav("/admin/jobs?stage=all")} testid="ops-total-jobs" />
          </div>
        </Card>

        <Card title="Today's throughput" icon={CheckCircle2} testid="ops-throughput">
          <Row label="Completed today" value={d.orders.completed_today} onClick={() => nav("/admin/jobs?stage=completed_today")} testid="ops-completed-today" />
        </Card>

        <Card title="Deadline attention" icon={Clock} testid="ops-deadline-attention">
          <Row label="Passed" value={na.passed} onClick={() => nav("/admin/jobs?risk=passed")} testid="ops-deadline-passed" />
          <Row label="Urgent" value={na.urgent} onClick={() => nav("/admin/jobs?risk=urgent")} testid="ops-deadline-urgent" />
          <Row label="Approaching" value={na.approaching} onClick={() => nav("/admin/jobs?risk=approaching")} testid="ops-deadline-approaching" />
          {(d.deadline.passed[0] || d.deadline.urgent[0]) && (
            <button data-testid="ops-deadline-top" onClick={() => nav(`/admin/jobs/${(d.deadline.passed[0] || d.deadline.urgent[0]).order_id}`)} className="mt-2 flex w-full items-center gap-2 rounded-lg border border-slate-100 p-2 text-left text-xs text-slate-500 hover:bg-slate-50">
              <span className="rounded bg-slate-200 px-1.5 py-0.5 font-semibold text-slate-600">TOP EXAMPLE</span>
              #{(d.deadline.passed[0] || d.deadline.urgent[0]).job_number}
              <DeadlineBadge status={(d.deadline.passed[0] || d.deadline.urgent[0]).deadline_status} days={(d.deadline.passed[0] || d.deadline.urgent[0]).days_to_deadline} />
            </button>
          )}
        </Card>

        <Card title="Open document requests" icon={FileQuestion} testid="ops-open-requests">
          <Row label="Server action required" value={d.open_requests.server_action} onClick={() => nav("/admin/requests?responsible=server")} testid="ops-requests-server" />
          <Row label="Admin review required" value={d.open_requests.admin_review} onClick={() => nav("/admin/requests?responsible=admin")} testid="ops-requests-admin" />
          <Row label="Client action required" value={d.open_requests.client_action} onClick={() => nav("/admin/requests?responsible=client")} testid="ops-requests-client" />
          <div className="mt-2 border-t border-slate-200 pt-1">
            <Row label="Total open requests" value={d.open_requests.count} onClick={() => nav("/admin/requests?status=requested")} testid="ops-requests-open" />
          </div>
          {d.open_requests.oldest && (
            <button data-testid="ops-oldest-request" onClick={() => nav(`/admin/jobs/${d.open_requests.oldest.order_id}`)} className="mt-2 block w-full rounded-lg border border-slate-100 p-2 text-left text-xs hover:bg-slate-50">
              Oldest: #{d.open_requests.oldest.job_number} · {d.open_requests.oldest.client_name} · {new Date(d.open_requests.oldest.created_at).toLocaleDateString()}
            </button>
          )}
        </Card>

        <Card title="Evidence accountability" icon={ShieldAlert} testid="ops-evidence" tone={(d.evidence?.integrity_review_required || d.evidence?.orphaned_evidence) ? "red" : "slate"}>
          <Row label="Integrity review required" value={d.evidence?.integrity_review_required ?? d.integrity_flags.count} onClick={() => nav("/admin/evidence-issues?type=integrity")} testid="ops-evidence-integrity" />
          <Row label="Missing required evidence" value={d.evidence?.missing_required_evidence ?? 0} onClick={() => nav("/admin/evidence-issues?type=missing_required")} testid="ops-evidence-missing" />
          <Row label="Orphaned / unavailable evidence" value={d.evidence?.orphaned_evidence ?? 0} onClick={() => nav("/admin/evidence-issues?type=orphaned")} testid="ops-evidence-orphaned" />
          {d.integrity_flags.items.slice(0, 3).map((f) => (
            <button key={f.id} onClick={() => f.order_id && nav(`/admin/jobs/${f.order_id}`)} className="block w-full truncate border-b border-slate-100 py-1.5 text-left text-xs text-red-700 last:border-0 hover:bg-red-50">
              {f.original_filename} {f.integrity_checked_at ? `· ${new Date(f.integrity_checked_at).toLocaleDateString()}` : ""}
            </button>
          ))}
        </Card>

        <Card title="Process Server onboarding" icon={UserCog} testid="ops-server-onboarding">
          <div className="grid grid-cols-2 gap-2">
            {[
              ["Total servers", d.server_onboarding.total, "all"],
              ["Account complete", d.server_onboarding.account_complete, "account_complete"],
              ["Documents in progress", d.server_onboarding.documents_in_progress, "docs_in_progress"],
              ["Fully doc-approved", d.server_onboarding.fully_document_approved, "docs_approved"],
              ["Pending account approval", d.server_onboarding.pending_account_approval, "pending_approval"],
              ["Approved / active", d.server_onboarding.approved_active, "active"],
              ["Rejected", d.server_onboarding.rejected, "rejected"],
              ["Missing documents", d.server_onboarding.missing_documents, "missing_docs"],
            ].map(([label, n, f]) => (
              <button key={f} data-testid={`funnel-${f}`} onClick={() => nav(`/admin/servers?filter=${f}`)}
                className="rounded-lg border border-slate-100 p-2.5 text-left transition hover:bg-slate-50">
                <p className="font-display text-xl font-extrabold text-slate-900">{n}</p>
                <p className="text-[11px] leading-tight text-slate-500">{label}</p>
              </button>
            ))}
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Document pipeline</p>
            <Row label="Total required outstanding" value={d.server_onboarding.documents.outstanding} onClick={() => nav("/admin/servers?filter=missing_docs")} testid="funnel-docs-outstanding" />
            <Row label="Documents pending review" value={d.server_onboarding.documents.pending_review} onClick={() => nav("/admin/servers?filter=docs_in_progress")} />
            <Row label="Documents approved" value={d.server_onboarding.documents.approved} onClick={() => nav("/admin/servers?filter=docs_approved")} />
            <Row label="Documents rejected" value={d.server_onboarding.documents.rejected} onClick={() => nav("/admin/servers?filter=rejected")} />
          </div>
        </Card>

        <Card title="Billing attention" icon={Receipt} testid="ops-billing-attention">
          <Row label="Outstanding invoices" value={`${d.billing.outstanding_count} · ${fmtMoney(cur, d.billing.outstanding_amount)}`} onClick={() => nav("/admin/billing")} testid="ops-billing-outstanding" />
          <Row label="Unpaid invoices" value={d.billing.unpaid_count} onClick={() => nav("/admin/billing")} />
          <Row label="Pending server payouts" value={`${d.billing.pending_compensation_count} · ${fmtMoney(cur, d.billing.pending_compensation_amount)}`} onClick={() => nav("/admin/billing")} />
          <Row label="Completed today" value={d.billing.completed_today} />
        </Card>
      </div>

      <div data-testid="ops-quick-actions" className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-display text-base font-bold text-slate-900">Quick actions</h3>
        <div className="flex flex-wrap gap-2">
          {[["Orders", "/admin/jobs"], ["Document Requests", "/admin/requests"], ["Documents", "/admin/documents"], ["Billing", "/admin/billing"], ["Servers", "/admin/servers"], ["Clients", "/admin/clients"]].map(([l, to]) => (
            <button key={to} data-testid={`quick-action-${to.split("/").pop()}`} onClick={() => nav(to)} className="rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-900 hover:text-white">
              {l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
